import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';
// Using inline SVGs with Tailwind classes instead of React Icons

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [loading, setLoading] = useState<boolean>(true);
  const [verificationComplete, setVerificationComplete] = useState<boolean>(false);
  const { user, getToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        if (!sessionId) {
          toast.error('Missing session information');
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        if (!user) {
          // Wait for auth to initialize before proceeding
          return;
        }

        const token = await getToken();
        if (!token) {
          toast.error('Authentication error');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Verify the payment session with our backend
        const response = await fetch(`${ENV.API_URL}/api/payment/verify-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            sessionId,
            userId: user.id
          })
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.details || 'Failed to verify payment');
        }

        setVerificationComplete(true);
        toast.success('Subscription activated successfully!');

        const paymentStatus = String(data.paymentStatus || '').toLowerCase();
        const amountTotal = Number(data.amountTotal);
        const currency = String(data.currency || '').toUpperCase();

        if (paymentStatus === 'paid' && Number.isFinite(amountTotal) && currency) {
          const fbq = (window as any).fbq as undefined | ((...args: any[]) => void);
          if (typeof fbq === 'function') {
            fbq('track', 'Purchase', {
              value: amountTotal / 100,
              currency
            });
          }
        }

        // Redirect to dashboard after successful verification
        setTimeout(() => navigate('/dashboard'), 3000);
      } catch (error) {
        console.error('Payment verification error:', error);
        toast.error(error instanceof Error ? error.message : 'Payment verification failed');
        setTimeout(() => navigate('/'), 5000);
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId, user, getToken, navigate]);

  return (
    <div className="container mx-auto px-4 py-12 max-w-3xl text-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
        {loading ? (
          <>
            <svg className="animate-spin h-12 w-12 text-blue-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <h1 className="text-3xl font-bold mb-4">Verifying your payment...</h1>
            <p className="text-lg text-gray-600 mb-8">
              Please wait while we confirm your subscription.
            </p>
          </>
        ) : verificationComplete ? (
          <>
            <svg className="mx-auto h-16 w-16 text-green-500 mb-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
            </svg>
            <h1 className="text-3xl font-bold mb-4">Payment Successful!</h1>
            <p className="text-lg text-gray-600 mb-8">
              Thank you for subscribing to Explanatum. Your account has been upgraded.
            </p>
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-8">
              <p className="text-blue-800 font-medium">
                You will be redirected to your dashboard in a few seconds...
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-blue-600 text-white py-3 px-8 rounded-xl font-medium hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold mb-4 text-red-500">Verification Failed</h1>
            <p className="text-lg text-gray-600 mb-8">
              We couldn't verify your payment. Please contact support if you believe this is an error.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={() => navigate('/payment')}
                className="bg-blue-600 text-white py-3 px-8 rounded-xl font-medium hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="bg-gray-100 text-gray-800 py-3 px-8 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Contact Support
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
